import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { ymdLocal } from '../lib/format.js';
import { CASH_DENOMS, denomQtyFromObj, denomObjFromQty, denomTotal, fmtCash } from '../lib/cash-count.js';

// ─── Home → Daily: enter today's drawer cash count ─────────────────────────
// Mobile equivalent of the iPad kiosk close-out, for ANY employee. Counts
// today's drawer for the currently-selected location and submits it (POST
// /api/cash-count/daily — no capability gate; the server enforces
// assigned-location). A manager still certifies it later. Renders a compact
// tile inline on Daily; opens its own modal so Dashboard only mounts one tag.
export function DailyCashCountEntry({ location, user }) {
  const headers = { 'x-user-id': String(user.id) };
  const today = ymdLocal(new Date());
  const [count, setCount] = useState(null);   // existing daily row (or null)
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState(CASH_DENOMS.map(() => 0));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function load() {
    if (!location?.id) return;
    try {
      const r = await api('GET', `/api/cash-count/daily?location_id=${location.id}&business_date=${today}`, null, headers);
      setCount(r?.count || r || null);
    } catch { setCount(null); }
  }
  useEffect(() => { load(); }, [location?.id, today]);

  const total = denomTotal(qty);
  const submitted = count && count.staff_drawer_total != null;
  const certified = count && count.certified_at;
  // CJ 2026-07-29: the person who submitted a count can reopen it the SAME day
  // even after it's been certified (found more cash / miscount). This widget only
  // ever shows today's count, so "same day" is implicit; the server re-checks.
  const isSubmitter = count && Number(count.submitted_by_user_id) === Number(user.id);

  function openModal() {
    setQty(denomQtyFromObj(count?.staff_denominations || {}));
    setErr('');
    setOpen(true);
  }

  async function reopen() {
    if (busy || !count?.id) return;
    setBusy(true); setErr('');
    try {
      await api('PUT', `/api/cash-count/daily/${count.id}/reopen`, {}, headers);
      await load();
      openModal();   // straight into editing the reopened count
    } catch (e) { setErr(e?.message || String(e)); }
    finally { setBusy(false); }
  }

  async function submit() {
    if (busy) return;
    if (total === 0 && !confirm('Total is $0.00 — submit anyway?')) return;
    setBusy(true); setErr('');
    try {
      await api('POST', '/api/cash-count/daily', {
        location_id: location.id,
        business_date: today,
        denominations: denomObjFromQty(qty),
        drawer_total: total,
      }, headers);
      await load();
      setOpen(false);
    } catch (e) { setErr(e?.message || String(e)); }
    finally { setBusy(false); }
  }

  if (!location?.id) return null;

  return (
    <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: 14 }}>💵 Today's cash count · {location.name}</div>
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
          {certified
            ? <span style={{ color: '#226b4a', fontWeight: 600 }}>Certified · {fmtCash(count.final_drawer_total ?? count.staff_drawer_total)}</span>
            : submitted
              ? <span style={{ color: '#8a6b1a', fontWeight: 600 }}>Submitted · {fmtCash(count.staff_drawer_total)} (awaiting review)</span>
              : 'Not counted yet'}
        </div>
        {err && !open && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 4 }}>{err}</div>}
      </div>
      {certified && isSubmitter ? (
        // Same-day correction by the person who counted: reopen (reverts the
        // certification, audited) then drop straight into the edit modal.
        <button onClick={reopen} disabled={busy}
          style={{ padding: '9px 16px', borderRadius: 8, border: 0, fontWeight: 700, fontSize: 13,
                   background: '#8a6b1a', color: '#fff', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
          {busy ? '…' : 'Reopen to fix'}
        </button>
      ) : (
        <button onClick={openModal} disabled={certified}
          style={{ padding: '9px 16px', borderRadius: 8, border: 0, fontWeight: 700, fontSize: 13,
                   background: certified ? '#e0e0e0' : '#b48a47', color: '#fff',
                   cursor: certified ? 'default' : 'pointer' }}>
          {certified ? 'Locked' : submitted ? 'Update count' : 'Enter count'}
        </button>
      )}

      {open && (
        <div onClick={() => !busy && setOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
                   display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 14, maxWidth: 560, width: '100%', margin: '24px auto', padding: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Today's cash count</div>
              <button onClick={() => !busy && setOpen(false)} aria-label="Close"
                style={{ background: 'none', border: 0, fontSize: 22, color: '#9e9e9e', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ fontSize: 12, color: '#9e9e9e', marginBottom: 12 }}>{location.name} · {today}</div>
            {err && <div style={{ background: '#fee', color: '#b91c1c', padding: 10, borderRadius: 8, fontSize: 12.5, marginBottom: 10 }}>{err}</div>}

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {CASH_DENOMS.map((d, i) => (
                  <tr key={d.key} style={{ borderBottom: '1px solid #fafafa' }}>
                    <td style={{ padding: '8px 6px', fontWeight: 600 }}>
                      {d.label}
                      {d.sub && <span style={{ fontSize: 11, color: '#9e9e9e', fontWeight: 400 }}> · {d.sub}</span>}
                    </td>
                    <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                      <input type="number" inputMode="numeric" min="0" step="1"
                        value={qty[i] || ''} placeholder="0"
                        onChange={(e) => {
                          const v = Math.max(0, Math.floor(Number(e.target.value) || 0));
                          setQty((cur) => { const nx = [...cur]; nx[i] = v; return nx; });
                        }}
                        style={{ width: 84, padding: '10px 8px', fontSize: 16, textAlign: 'right',
                                 border: '1px solid #d0d4d9', borderRadius: 6, background: '#fdfcf7', fontVariantNumeric: 'tabular-nums' }} />
                    </td>
                    <td style={{ padding: '8px 6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                                 color: qty[i] * d.value === 0 ? '#bbb' : '#212121', fontWeight: 500, minWidth: 84 }}>
                      {fmtCash(qty[i] * d.value)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #b48a47', background: '#f6f4ee' }}>
                  <td style={{ padding: '12px 6px', fontSize: 12, color: '#6b6b6b', textTransform: 'uppercase', letterSpacing: 0.6 }}>Drawer total</td>
                  <td></td>
                  <td style={{ padding: '12px 6px', textAlign: 'right', fontSize: 20, fontWeight: 800, color: '#2f5d50', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtCash(total)}
                  </td>
                </tr>
              </tfoot>
            </table>

            <button onClick={submit} disabled={busy}
              style={{ marginTop: 14, width: '100%', padding: '13px', borderRadius: 10, border: 0,
                       fontWeight: 700, fontSize: 15, background: '#226b4a', color: '#fff', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
              {busy ? 'Submitting…' : submitted ? 'Update count' : 'Submit count'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
